import vuexI18n from "vuex-i18n";
import { Store, Module } from "vuex";

const namespace = "i18nV2";
let storeRef: Store<any> | null = null;
let isInited = false;

type AnyState = any;

function createVuexI18nModule(): Module<AnyState, AnyState> {
    return {
        namespaced: true,

        // copying all (actions/mutations/getters/namespaced)
        ...vuexI18n.store,
        // but the state must be new
        state: () => ({
            locale: null,
            fallback: null,
            translations: {},
        }),
    } as Module<AnyState, AnyState>;
}

function getByPath(obj: any, path: string): unknown {
    if (!obj) return undefined;
    if (Object.prototype.hasOwnProperty.call(obj, path)) {
        return obj[path];
    }

    return path.split(".").reduce((acc, k) => {
        return acc ? acc[k] : undefined;
    }, obj);
}

function interpolate(
    template: string,
    params?: Record<string, unknown>
): string {
    if (!params) {
        return template;
    }

    return template.replace(/{(\w+)}/g, (_, k) =>
        params[k] !== undefined ? String(params[k]) : `{${k}}`
    );
}

export const i18n = {
    init(store: Store<any>) {
        if (isInited) {
            return;
        }

        if (!store) {
            throw new Error("i18n.init(store) requires Vuex store");
        }

        storeRef = store;

        storeRef.registerModule(namespace, createVuexI18nModule());

        isInited = true;
    },

    t(key: string, params?: Record<string, unknown>): string {
        if (!storeRef) {
            throw new Error("i18n not initialized");
        }

        const state = storeRef.state[namespace];
        if (!state) {
            return key;
        }

        const { locale, fallback, translations } = state;

        const value =
            getByPath(translations?.[locale], key) ??
            (fallback ? getByPath(translations?.[fallback], key) : undefined);

        if (typeof value !== "string") {
            return interpolate(key, params);
        }

        return interpolate(value, params);
    },

    getLocale(): string {
        if (!storeRef) {
            throw new Error("i18n initialization failed");
        }

        return storeRef.state.i18n && storeRef.state.i18n.locale;
    },

    setLocale(locale: string) {
        if (!storeRef) {
            throw new Error("i18n is not initialized");
        }

        return storeRef.dispatch(namespace + "/setLocale", {
            locale,
        }) as Promise<void>;
    },

    add(locale: string, translations: any) {
        if (!storeRef) {
            throw new Error("i18n is not initialized");
        }

        return storeRef.dispatch(namespace + "/addLocale", {
            locale,
            translations,
        }) as Promise<void>;
    },
};

export function ttt(key: string, params?: Record<string, unknown>): string {
    return i18n.t(key, params);
}

export type I18N = typeof i18n;
